import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import styles from 'components/UI/V3/Drawer/Drawer.module.scss';
import { DrawerProvider } from 'components/UI/V3/Drawer/DrawerContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const ANIMATION_DURATION = 400;

interface DrawerProps {
    children: React.ReactNode;
    zIndex?: number;
    wrapperClassName?: string;
    /** Keeps the drawer (and its content) mounted while another full window is shown instead. */
    hidden?: boolean;
    onClose?: () => void;
    onBeforeClose?: () => boolean; // Return true to allow close, false to prevent
}

export const Drawer: React.FC<DrawerProps> = ({
    children,
    zIndex,
    wrapperClassName,
    hidden,
    onClose,
    onBeforeClose
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Auto-animate in on mount
    useEffect(() => {
        setIsVisible(true);
        requestAnimationFrame(() => {
            setIsAnimating(true);
        });
    }, []);

    const closeHandler = useCallback(() => {
        // Check if close is allowed
        if (onBeforeClose) {
            const canClose = onBeforeClose();
            if (!canClose) {
                // Prevent closing
                return;
            }
        }

        setIsAnimating(false);

        const timeout = setTimeout(() => {
            setIsVisible(false);
            onClose?.();
        }, ANIMATION_DURATION);

        return () => clearTimeout(timeout);
    }, [onClose, onBeforeClose]);

    const contextValue = useMemo(() => ({ close: closeHandler }), [closeHandler]);

    if (!isVisible) return null;

    return createPortal(
        <DrawerProvider value={contextValue}>
            <div
                className={clsx(styles.root, { [styles.open]: isAnimating })}
                style={
                    {
                        '--animation-duration': `${ANIMATION_DURATION}ms`,
                        ...(zIndex !== undefined && { zIndex }),
                        ...(hidden && { display: 'none' })
                    } as React.CSSProperties
                }
            >
                <div className={styles.drawer}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={closeHandler}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>

                    <div className={clsx(styles.wrapper, wrapperClassName)}>{children}</div>
                </div>

                <div
                    className={styles.backdrop}
                    onClick={closeHandler}
                />
            </div>
        </DrawerProvider>,
        document.body
    );
};
